import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { LogOut, User, Settings } from "lucide-react";

import ProtectedRoute from "./components/ProtectedRoute";
import Kuralattam3 from "./Kuralattam3";
import Kuralattam2 from "./Kuralattam2";
import Kuralattam1 from "./Kuralattam1";
import GameHome from "./GameHome";
import Home from "./home";
import Registration from "./registration";
import Login from "./login";

import Users from "./User/List";
import AdminDashboard from "./admin";
import { AuthProvider, useAuth } from "./contexts/AuthProvider";
import Profile from "./components/Profile";
// Add this import at the top with other imports
import UnauthorizedPage from "./components/UnauthorizedPage";
import SelectionPage from "./components/SelectionPage";

// Loading component to display during suspense
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-screen w-full">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
    <p className="ml-2 text-white text-lg">Loading...</p>
  </div>
);

function App() {
  // Add ref for user dropdown

  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-100 flex flex-col ">
          <main className="flex-grow w-full flex   bg-gradient-to-bl from-[#0f172a] via-[#1e1a78] to-[#0f172a]">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<GameLayout />}>
                  <Route index element={<Home />} />
                  <Route path="/registration" element={<Registration />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                </Route>

                {/* Change from Layout to GameLayout to remove header */}
                <Route path="/selection" element={<GameLayout />}>
                  <Route
                    path="/selection"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <SelectionPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Add the profile route here */}
                <Route path="/profile" element={<Layout />}>
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute allowedRoles={["User", "Admin"]}>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="/aattam" element={<Layout />}>
                  <Route
                    path="/aattam"
                    element={
                      <ProtectedRoute allowedRoles={["User", "Admin"]}>
                        <GameHome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/aattam/1"
                    element={
                      <ProtectedRoute allowedRoles={["User", "Admin"]}>
                        <Kuralattam1 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/aattam/2"
                    element={
                      <ProtectedRoute allowedRoles={["User", "Admin"]}>
                        <Kuralattam2 />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route
                  path="/aattam/2"
                  element={
                    <ProtectedRoute allowedRoles={["User", "Admin"]}>
                      <Kuralattam2 />
                    </ProtectedRoute>
                  }
                />
                <Route path="/aattam/3" element={<Layout />}>
                  <Route
                    path="/aattam/3"
                    element={
                      <ProtectedRoute allowedRoles={["User", "Admin"]}>
                        <Kuralattam3 />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="/admin" element={<GameLayout />}>
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="/users" element={<Layout />}>
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Users />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Routes>
            </Suspense>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

function Layout() {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth(); // Add this line to access the user context

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    //console.log("App.js= > user", user);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    //const result = await logout();
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="w-full">
      <header className="w-full">
        <div className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 p-4 text-center text-white relative z-50">
          <div>
            <h3 className="text-4xl font-bold tracking-widest">
              {"குறளாட்டம் கொண்டாட்டம்"}
            </h3>
            <h3 className="text-lg text-blue-200 text-center tracking-widest pt-1">
              {"Powered by Vetri Academy Kuppakudi"}
            </h3>
          </div>

          {/* User dropdown */}
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <div className="ml-3 relative" ref={dropdownRef}>
              <div>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="ml-2 text-gray-700 hidden sm:block">
                    {user && user.name}
                  </span>
                  <svg
                    className="ml-1 h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              {isDropdownOpen && (
                <div
                  className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                  ref={dropdownRef}
                >
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate("/profile");
                    }}
                    className="flex w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 items-center"
                  >
                    <User size={16} className="mr-2" />
                    Profile
                  </button>
                  {user && user.UserRole === "Admin" && (
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate("/admin");
                      }}
                      className="flex w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 items-center"
                    >
                      <Settings size={16} className="mr-2" />
                      Admin Console
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 items-center"
                  >
                    <LogOut size={16} className="mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="m-5">
        <Outlet />
      </div>
    </div>
  );
}

function GameLayout() {
  return (
    <div className="w-full ">
      <Outlet />
    </div>
  );
}
export default App;
